import React, { useState, useEffect, useRef } from "react";
import { Table, Col, Breadcrumb, Button, Space, Row } from "antd";
import { Link, useParams } from "react-router-dom";
import { DownloadOutlined } from "@ant-design/icons";
import plogo from "../../image/plogo.png";
import { getRequest } from "../../axios";
import PropTypes from "prop-types";
import ButtonField from "../../common/htmlComponents/ButtonField";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";
import newLogo from "../../image/PROCEM3.svg";

const PurchaseOrderBySite = () => {
  const [orderData, setOrderData] = useState(null);

  const { id, siteName } = useParams();
  const buttonRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const pdfContainerRef = useRef(null);

  const units = [
    "",
    "ONE",
    "TWO",
    "THREE",
    "FOUR",
    "FIVE",
    "SIX",
    "SEVEN",
    "EIGHT",
    "NINE",
  ];
  const tens = [
    "",
    "TEN",
    "TWENTY",
    "THIRTY",
    "FORTY",
    "FIFTY",
    "SIXTY",
    "SEVENTY",
    "EIGHTY",
    "NINETY",
  ];
  const teens = [
    "TEN",
    "ELEVEN",
    "TWELVE",
    "THIRTEEN",
    "FOURTEEN",
    "FIFTEEN",
    "SIXTEEN",
    "SEVENTEEN",
    "EIGHTEEN",
    "NINETEEN",
  ];

  const zero = "ZERO";
  const arab = "ARAB";
  const crore = "CRORE";
  const lakh = "LAKH";
  const thousand = "THOUSAND";
  const hundred = "HUNDRED";
  const currency = "RUPEES";
  const paisa = "PAISA";
  const only = "ONLY";

  const convertNumberToWords = (amount) => {
    if (amount === 0) return `${zero} ${currency} ${only}`;

    function convert(num) {
      let parts = [];
      if (num >= 1e9) {
        parts.push(`${convert(Math.floor(num / 1e9))} ${arab}`);
        num %= 1e9;
      }
      if (num >= 1e7) {
        parts.push(`${convert(Math.floor(num / 1e7))} ${crore}`);
        num %= 1e7;
      }
      if (num >= 1e5) {
        parts.push(`${convert(Math.floor(num / 1e5))} ${lakh}`);
        num %= 1e5;
      }
      if (num >= 1000) {
        parts.push(`${convert(Math.floor(num / 1000))} ${thousand}`);
        num %= 1000;
      }
      if (num >= 100) {
        parts.push(`${convert(Math.floor(num / 100))} ${hundred}`);
        num %= 100;
      }
      if (num >= 20) {
        parts.push(`${tens[Math.floor(num / 10)]}`);
        if (num % 10 > 0) parts.push(units[num % 10]);
      } else if (num >= 10) {
        parts.push(`${teens[num - 10]}`);
      } else if (num > 0) {
        parts.push(`${units[num]}`);
      }
      return parts.join(" ");
    }

    let integerPart = Math.floor(amount);
    let wholeWordPart = convert(integerPart);
    let result = wholeWordPart ? `${wholeWordPart} ${currency}` : "";

    let decimalPart = Math.round((amount - integerPart) * 100);
    if (decimalPart > 0) {
      if (wholeWordPart) {
        result += " AND ";
      }
      result += `${convert(decimalPart)} ${paisa}`;
    }

    return `${result} ${only}`;
  };
  console.log("orderDatacx5", orderData);

  useEffect(() => {
    const getPoOrder = async () => {
      try {
        const response = await getRequest(
          `po-order/${id}/po-view-by-purchase-requisition/?site_name=${encodeURIComponent(siteName)}`
        );

        setOrderData(response.data.data);
      } catch (error) {
        console.error("Error:", error);
      }
    };
    getPoOrder();
  }, []);

  if (!orderData) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <span>Loading...</span>
      </div>
    );
  }

  // --- Columns and Data (keep as is, but add Unit Rate, Basic Amount) ---
  let columns = [
    { title: "Sr.", dataIndex: "SrNo", width: "50px" },
    { title: "Item Description", dataIndex: "ItemDescription" },
    { title: "HSN", dataIndex: "HSN" },
  ];

  // Add more columns if needed (e.g., Diameter, Unit Rate, Basic Amount)
  columns = columns.concat([
    { title: "Qty.", dataIndex: "QTY", align: 'center'},
    {
      title: "UoM.",
      dataIndex: "UOM",
      align: "center",
      render: (text) => (text === "Cubic Meter" ? "Cu. Mtr." : text),
    },
    { title: "Delivery Date", dataIndex: "DeliveryDate" },
    { title: "Tax(%)", dataIndex: "Tax", align: "center", },
    { title: "Unit Rate", dataIndex: "UnitRate", align: "right", },
    { title: "Basic Amount", dataIndex: "BasicAmount", align: "right", },
  ]);

  // Prepare data for the table
  const data = orderData.material_description.map((item, index) => ({
    SrNo: index + 1,
    ItemDescription: item.material_name,
    HSN: item.hsn_code,
    QTY: item.qty,
    UOM: item.uom,
    DeliveryDate: item.delivery_date,
    Tax: item.tax,
    UnitRate: item.basic_unit_price ? `₹ ${item.basic_unit_price}` : "",
    BasicAmount: item.basic_amount
      ? `₹ ${item.basic_amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`
      : "",
  }));

  const downloadAsPDF = () => {
    if (pdfContainerRef.current) {
      setIsDownloading(true);

      if (buttonRef.current) {
        buttonRef.current.classList.add("hide-in-pdf");
      }

      setTimeout(() => {
        html2canvas(pdfContainerRef.current, {
          useCORS: true,
          scale: 2,
          logging: false,
        })
          .then((canvas) => {
            const imgWidth = 210; // A4 width in mm
            const pageHeight = 297; // A4 height in mm

            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;

            const imgHeightPerPage = (pageHeight * canvasWidth) / imgWidth;
            let position = 0;

            const pdf = new jsPDF("p", "mm", "a4");

            while (position < canvasHeight) {
              const pageCanvas = document.createElement("canvas");
              pageCanvas.width = canvasWidth;
              pageCanvas.height = Math.min(
                imgHeightPerPage,
                canvasHeight - position
              );

              const ctx = pageCanvas.getContext("2d");
              ctx.drawImage(
                canvas,
                0,
                position,
                canvasWidth,
                pageCanvas.height,
                0,
                0,
                canvasWidth,
                pageCanvas.height
              );

              const imgData = pageCanvas.toDataURL("image/png");

              if (position > 0) pdf.addPage();
              pdf.addImage(
                imgData,
                "PNG",
                0,
                0,
                imgWidth,
                (pageCanvas.height * imgWidth) / canvasWidth
              );

              position += imgHeightPerPage;
            }

            pdf.save(`Preview Purchase Order-${id}.pdf`);

            if (buttonRef.current) {
              buttonRef.current.classList.remove("hide-in-pdf");
            }
            setIsDownloading(false);
          })
          .catch((error) => {
            console.error("Error generating PDF:", error);
            if (buttonRef.current) {
              buttonRef.current.classList.remove("hide-in-pdf");
            }
            setIsDownloading(false);
          });
      }, 500);
    } else {
      console.error("PDF container reference is not defined.");
    }
  };

  return (
    <>
      <div className="invoice-container">
        <div className="ListingTopBar">
          <div className="pagenamewrap">
            <Space>
              <ButtonField
                className="BlueShadowBtn"
                type="primary"
                icon={<DownloadOutlined />}
                label={isDownloading ? "Downloading..." : "Download PDF"}
                onClick={downloadAsPDF}
                ref={buttonRef}
              >
                Download
              </ButtonField>
            </Space>
          </div>
        </div>

        <section className="grid-sec mt-5" ref={pdfContainerRef}>
          <div className="PR-body">
            <div
              style={{
                fontFamily: "Arial, sans-serif",
                fontSize: "13px",
                color: "#333",
                lineHeight: "0.9",
              }}
            >
              <h4
                style={{
                  marginLeft: "10px",
                  borderBottom: "1px solid #bcb6b6",
                }}
              >
                Purchase Order
              </h4>
              <div
                style={{ display: "flex", borderBottom: "1px solid #bcb6b6" }}
              >
                <div
                  style={{
                    width: "33.33%",
                    padding: "10px",
                    boxSizing: "border-box",
                  }}
                >
                  <h4 style={{ marginTop: 0 }}>Buyer Details</h4>
                  <p>
                    <strong>Name:</strong> {orderData.company_details[0].name}
                  </p>
                  <p>
                    <strong>Mobile:</strong> {orderData.company_details[0].phone_number}
                  </p>
                  <p>
                    <strong>Email:</strong> {orderData.company_details[0].email}
                  </p>
                  <p>
                    <strong>Address:</strong> {`${orderData.company_details[0].registered_business_address_building}, ${orderData.company_details[0].registered_business_address_area}, ${orderData.company_details[0].registered_business_address_city}`}
                  </p>
                  <p>
                    <strong>GSTN:</strong> {orderData.company_details[0].gst_number}
                  </p>
                </div>

                <div
                  style={{
                    width: "33.33%",
                    padding: "10px",
                    boxSizing: "border-box",
                  }}
                >
                  <h4 style={{ marginTop: 0 }}>PO Details</h4>
                  <p>
                    <strong>PO Number:</strong> {orderData.po_number}
                  </p>
                  <p>
                    <strong>PO Date:</strong> {orderData.po_created_date ? moment(orderData.po_created_date, "DD-MM-YYYY").format("DD/MM/YYYY") : ""}
                  </p>
                  <p>
                    <strong>Payment Terms:</strong> {orderData.payment_terms}
                  </p>
                  <p>
                    <strong>Delivery Date:</strong> {orderData.material_description[0]?.delivery_date || "N/A"}
                  </p>
                </div>

                <div
                  style={{
                    width: "33.33%",
                    textAlign: "center",
                    padding: "10px",
                    boxSizing: "border-box",
                  }}
                >
                  <img
                    src={orderData.company_details[0].company_logo || newLogo}
                    alt="Company Logo"
                    style={{
                      height: "80px",
                      maxWidth: "100%",
                      objectFit: "contain",
                    }}
                  />
                </div>
              </div>

              <div
                style={{ display: "flex", borderBottom: "1px solid #bcb6b6" }}
              >
                <div
                  style={{
                    width: "33.33%",
                    padding: "10px",
                    boxSizing: "border-box",
                  }}
                >
                  <h4 style={{ marginTop: 0 }}>Vendor Details</h4>
                  <p>
                    <strong>Name:</strong> {orderData.vendor_details[0].name}
                  </p>
                  <p>
                    <strong>Mobile:</strong> {orderData.vendor_details[0].phone_number}
                  </p>
                  <p>
                    <strong>Email:</strong> {orderData.vendor_details[0].email}
                  </p>
                  <p>
                    <strong>Address:</strong> {`${orderData.vendor_details[0].registered_business_address_building}, ${orderData.vendor_details[0].registered_business_address_area}, ${orderData.vendor_details[0].registered_business_address_city}`}
                  </p>
                  <p>
                    <strong>GSTN:</strong> {orderData.vendor_details[0].gst_number}
                  </p>
                </div>

                <div
                  style={{
                    width: "33.33%",
                    padding: "10px",
                    boxSizing: "border-box",
                  }}
                >
                  <h4 style={{ marginTop: 0 }}>Billing Details</h4>
                  <p>
                    <strong>City:</strong> {orderData.site_location[0].billing_details.billing_city}
                  </p>
                  <p>
                    <strong>Address:</strong> {`${orderData.site_location[0].billing_details.billing_address_line_1}, ${orderData.site_location[0].billing_details.billing_address_line_2}, ${orderData.site_location[0].billing_details.billing_city}, ${orderData.site_location[0].billing_details.billing_state}, ${orderData.site_location[0].billing_details.billing_pincode}`}
                  </p>
                  <p>
                    <strong>GSTN:</strong> {orderData.site_location[0].gst_number}
                  </p>
                </div>

                <div
                  style={{
                    width: "33.33%",
                    padding: "10px",
                    boxSizing: "border-box",
                  }}
                >
                  <h4 style={{ marginTop: 0 }}>Shipping Details</h4>
                  <p>
                    <strong>City:</strong> {orderData.site_location[0].shipping_details.shipping_city}
                  </p>
                  <p>
                    <strong>Address:</strong> {`${orderData.site_location[0].shipping_details.shipping_address_line_1}, ${orderData.site_location[0].shipping_details.shipping_address_line_2}, ${orderData.site_location[0].shipping_details.shipping_city}, ${orderData.site_location[0].shipping_details.shipping_state}, ${orderData.site_location[0].shipping_details.shipping_pincode}`}
                  </p>
                  <p>
                    <strong>GSTN:</strong> {orderData.site_location[0].gst_number}
                  </p>
                </div>
              </div>
            </div>

            <div className="hltxt" style={{ marginTop: "4px" }}>
              <strong>
                Reference: Vendor Final Offer : {orderData.po_number} | {orderData.material_description[0]?.delivery_date}
              </strong>
            </div>
            <Table
              className="poordertbl"
              columns={columns}
              dataSource={data}
              pagination={false}
              rowClassName={() => "custom-row-height"}
            />

            <div className="invofooter po1">
              <table>
                <tbody>
                  <tr>
                    <th>
                      <b>Basic Sub Total:</b>
                    </th>
                    <td>
                      <b>
                        ₹ {orderData.total[0].basic_sub_total?.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                      </b>
                    </td>
                  </tr>
                  <tr>
                    <th>CGST:</th>
                    <td>
                      ₹ {orderData.total[0].cgst?.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </td>
                  </tr>
                  <tr>
                    <th>SGST:</th>
                    <td>
                      ₹ {orderData.total[0].sgst?.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </td>
                  </tr>
                  <tr>
                    <th className="ttlbg">
                      <b>Total:</b>
                    </th>
                    <td className="ttlbg">
                      <b>
                        ₹ {orderData.total[0].total?.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                      </b>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="hltxt">
              AMOUNT IN WORDS: <b>{convertNumberToWords(orderData.total[0].total.toFixed(0))}</b>
            </div>
            <Row gutter={[15]}>
              <Col xs={24} sm={24} md={12}>
                <div className="orderpdtl ftrsigntbl" style={{ marginLeft: "12px" }}>
                  <h3>
                    <label>Other Terms:</label> The Seller has accepted order unconditionally.
                  </h3>
                  <p>
                    <strong>For, {orderData.company_details[0].name}</strong>
                  </p>
                  <br />
                  <p className="authsign">Authorised Signatory</p>
                </div>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <div className="orderpdtl ftrsigntbl rightalign">
                  <p style={{ textAlign: "start", marginLeft: "67%" }}>
                    <strong>For, {orderData.vendor_details[0].name}</strong>
                  </p>
                  <br />
                  <p className="authsign">Authorised Signatory</p>
                </div>
              </Col>
            </Row>

            <div className="details-container">
              <div className="details-wrapper">
                <div className="key-person-section">
                  <h4 className="section-title">Billing Key Person</h4>
                  <table className="details-table">
                    <thead>
                      <tr className="table-header">
                        <th>Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderData.site_location[0].billing_details.billing_keypersons.map(
                        (person, index) => (
                          <tr key={index}>
                            <td>{person.name ? person.name : "NA"}</td>
                            <td>{person.email ? person.email : "NA"}</td>
                            <td>{person.mobile_number ? person.mobile_number : "NA"}</td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="key-person-section">
                  <h4 className="section-title">Shipping Key Person</h4>
                  <table className="details-table">
                    <thead>
                      <tr className="table-header">
                        <th>Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderData.site_location[0].shipping_details.shipping_keypersons.map(
                        (person, index) => (
                          <tr key={index}>
                            <td>{person.name ? person.name : "NA"}</td>
                            <td>{person.email ? person.email : "NA"}</td>
                            <td>{person.mobile_number ? person.mobile_number : "NA"}</td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

PurchaseOrderBySite.propTypes = {
  id: PropTypes.number,
  siteName: PropTypes.string,
};

export default PurchaseOrderBySite;
